import React, { useState, useEffect } from "react";
import { Paper, Text, Box, Alert } from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import ImageViewer from "@/components/ImageViewer/ImageViewer";
import PDFViewer from "@/components/PdfViewer/PDFViewer";
import WordViewer from "@/components/WordViewer/WordViewer";
import type { SelectedDoc } from "../../types";
import SpreadsheetViewer from "@components/SpreadsheetViewer/SpreadsheetViewer.tsx";
import { isMobileDevice } from "@utils/deviceDetection";

interface TaxInfoDocPreviewProps {
  selectedDoc: SelectedDoc;
}

const TaxInfoDocPreview: React.FC<TaxInfoDocPreviewProps> = ({
  selectedDoc,
}) => {
  const [hasError, setHasError] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    setIsMobile(isMobileDevice());
  }, []);

  const handlePdfError = () => {
    setHasError(true);
    notifications.show({
      title: "Invalid PDF",
      message:
        "The PDF file could not be loaded. It may be corrupted or have an invalid structure.",
      color: "red",
    });
  };

  const renderContent = (): JSX.Element => {
    const label = selectedDoc?.label?.toLowerCase();
    const docUrl = selectedDoc.presignedUrl ?? "";

    if (hasError && label?.endsWith(".pdf")) {
      return (
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Failed to load PDF"
          color="red"
        >
          The PDF file could not be loaded. It may be corrupted or have an
          invalid structure.
        </Alert>
      );
    }

    if (label?.endsWith(".pdf")) {
      return (
        <PDFViewer
          file={docUrl}
          onError={handlePdfError}
          simplifiedControls={isMobile}
          disableDrag={isMobile}
        />
      );
    }
    if (label?.endsWith(".csv")) {
      return <SpreadsheetViewer fileUrl={docUrl} key={docUrl} />;
    }
    if (label?.endsWith(".doc") || label?.endsWith(".docx")) {
      return (
        <WordViewer
          fileUrl={docUrl}
          fileName={selectedDoc.label || undefined}
          key={docUrl}
        />
      );
    }
    return <ImageViewer src={docUrl} />;
  };

  return (
    <Box>
      {/* Document title header - Blue box */}
      <Paper
        withBorder
        style={{
          backgroundColor: "var(--mantine-color-blue-0)",
          padding: "1rem",
          marginBottom: 0,
          borderRadius: "8px 8px 0 0",
        }}
      >
        <Text size="sm" fw={500} ta="center">
          {selectedDoc.label}
        </Text>
      </Paper>

      {/* Document viewer - Controls will appear here, outside the blue box */}
      <Paper
        withBorder
        style={{
          display: "flex",
          flexDirection: "column",
          borderTop: "none",
          borderRadius: "0 0 8px 8px",
        }}
      >
        <div className="preview-content">{renderContent()}</div>
      </Paper>
    </Box>
  );
};

export default TaxInfoDocPreview;
